import React, { useEffect, useState } from 'react';
import { Gift, X } from 'lucide-react';

const TEAL = '#02857E';
const TEAL_DEEP = '#014A46';
const INK = '#04211F';
const YELLOW = '#F7CF0D';
const DISMISS_KEY = 'lr_promo_2nd_month_dismissed_v1';

export const SECOND_MONTH_FREE_HEADLINE = "Your Second Month's on Us";
export const SECOND_MONTH_FREE_BODY =
  'Join LaneRunner now, pay for month one, get month two free. Cancel anytime.';
export const SECOND_MONTH_FREE_BADGE = '2nd month free';

/**
 * Promotional banner / badge for the "second month free" offer.
 *
 * DISPLAY AND COPY ONLY. The free month is granted manually as a Stripe coupon
 * per signup — this component contains no billing, checkout or coupon logic.
 */
interface SecondMonthFreePromoProps {
  /** Show an X and remember the dismissal in localStorage. */
  dismissible?: boolean;
  className?: string;
}

export const SecondMonthFreePromo: React.FC<SecondMonthFreePromoProps> = ({
  dismissible = false,
  className,
}) => {
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!dismissible) return;
    try {
      if (localStorage.getItem(DISMISS_KEY) === '1') setHidden(true);
    } catch {
      /* storage unavailable — just show the banner */
    }
  }, [dismissible]);

  if (hidden) return null;

  const dismiss = () => {
    setHidden(true);
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      className={`relative max-w-6xl mx-auto rounded-3xl overflow-hidden shadow-[0_20px_45px_-25px_rgba(1,74,70,0.75)] ${className || ''}`}
      style={{
        background: `linear-gradient(115deg, ${TEAL} 0%, ${TEAL_DEEP} 58%, ${INK} 100%)`,
      }}
    >
      {/* signature element: yellow ribbon corner */}
      <div
        className="absolute top-0 right-0 w-28 h-28 pointer-events-none"
        aria-hidden
        style={{
          background: `linear-gradient(225deg, ${YELLOW} 0%, ${YELLOW} 42%, transparent 42.5%)`,
          opacity: 0.95,
        }}
      />
      {/* soft glow */}
      <div
        className="absolute -left-16 -bottom-20 w-64 h-64 rounded-full pointer-events-none"
        aria-hidden
        style={{ background: 'rgba(247,207,13,0.14)', filter: 'blur(10px)' }}
      />

      <div className="relative flex flex-col md:flex-row md:items-center gap-6 px-6 py-7 sm:px-10 sm:py-9">
        {/* gift tag device */}
        <div className="flex-shrink-0">
          <div
            className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center rotate-[-6deg] shadow-lg"
            style={{ backgroundColor: YELLOW }}
          >
            <Gift className="w-7 h-7 sm:w-8 sm:h-8" style={{ color: INK }} strokeWidth={2.2} />
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <span
            className="inline-flex items-center px-3 py-1 rounded-full text-[10px] sm:text-[11px] font-extrabold uppercase tracking-[0.18em]"
            style={{ backgroundColor: YELLOW, color: INK }}
          >
            Limited time
          </span>

          <h3 className="mt-3 text-2xl sm:text-4xl font-extrabold tracking-tight text-white leading-[1.1]">
            {SECOND_MONTH_FREE_HEADLINE}
          </h3>
          <span
            className="mt-2 block h-[3px] w-16 rounded-full"
            style={{ backgroundColor: YELLOW }}
            aria-hidden
          />
          <p className="mt-3 text-sm sm:text-base text-white/85 max-w-xl">
            {SECOND_MONTH_FREE_BODY}
          </p>
        </div>

        {/* value framing */}
        <div className="md:pl-6 md:border-l md:border-white/15 md:self-stretch flex md:flex-col justify-center items-start md:items-center gap-1 flex-shrink-0">
          <p
            className="text-3xl sm:text-4xl font-extrabold leading-none"
            style={{ color: YELLOW }}
          >
            50%
          </p>
          <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-wider text-white/75 md:text-center">
            off your first
            <br className="hidden md:block" /> two months
          </p>
        </div>

        {dismissible && (
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss offer"
            className="absolute top-3 left-3 md:left-auto md:right-3 md:top-3 p-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

/** Designed "deal sticker" pill for pricing cards. */
export const SecondMonthFreeBadge: React.FC<{ className?: string }> = ({ className }) => (
  <span
    className={`inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-md text-[10px] font-extrabold uppercase tracking-[0.08em] whitespace-nowrap shadow-[0_2px_8px_-2px_rgba(1,74,70,0.45)] ring-1 ring-black/5 ${className || ''}`}
    style={{
      background: `linear-gradient(135deg, ${YELLOW} 0%, #FFE256 100%)`,
      color: INK,
    }}
  >
    <Gift className="w-3 h-3" strokeWidth={2.6} />
    2nd month free
  </span>
);

export default SecondMonthFreePromo;
